// @flow

import React, { useState } from "react";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "@material-ui/core/Button";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";


type EmailNotificationFormPropsType = {
  licenceId: string,
  emailList: Array<string>,
  days: number,
  isNotified: boolean,
  onSubmit: (data: {
    licence_uuid: string,
    email_list: Array<string>,
    days: number,
    is_notified: boolean
  }) => void,
  notificationUuid?: string,
  onDelete?: (uuid: string) => void
};


function AssignEmailNotificationForm(props: EmailNotificationFormPropsType): React.Node {
  const {
    licenceId,
    emailList = [],
    days: priorDays = 0,
    isNotified = false,
    onSubmit,
    notificationUuid,
    onDelete
  } = props;
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState(emailList || []);
  const [days, setDays] = useState(priorDays);
  const [notified, setNotified] = useState(isNotified);
  const [emailError, setEmailError] = useState("");
  const [formError, setFormError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const validateEmail = (email: string): boolean => {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  };

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(trimmed)) {
      setEmailError("Invalid email address");
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailError("Email already added");
      return;
    }
    setEmails([...emails, trimmed]);
    setEmailInput("");
    setEmailError("");
  };

  const removeEmail = (idx: number) => {
    setEmails(emails.filter((_: string, i: number): boolean => i !== idx));
    setEmailError("");
  };

  const handleEmailInputChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value);
    setEmailError("");
  };

  const handleEmailInputKeyDown = (e: SyntheticKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmail();
    }
  };

  const handleDaysChange = e => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
      setDays(val);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    setFormError("");

    if (!licenceId) {
      setFormError("Licence ID is missing.");
      return;
    }

    if (emails.length === 0) {
      setFormError("At least one email is required.");
      return;
    }

    if (!days || parseInt(days, 10) < 0) {
      setFormError("Days must be a positive number.");
      return;
    }

    // if (!licenceId || emails.length === 0 || !days || parseInt(days, 10) < 0) return;
    onSubmit && onSubmit({ licence_uuid: licenceId, email_list: emails, days: parseInt(days, 10), is_notified: notified });
  };

  return (
    <div style={{ padding: 16 }}>
      <form onSubmit={handleSubmit} autoComplete="off">
        <h2>Email Notification Settings</h2>
        {formError && (
          <div style={{ color: "red", marginBottom: 16 }}>{formError}</div>
        )}
        <div style={{ marginBottom: 16 }}>
          <TextField
            label="Licence ID"
            value={licenceId}
            InputProps={{ readOnly: true }}
            margin="normal"
            fullWidth
            variant="outlined"
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <FormLabel component="legend">Email List</FormLabel>
          <List dense>
            {emails.map((email: string, idx: number): React.Node => (
              <ListItem key={idx} divider style={{ display: "flex", alignItems: "center" }}>
                <div style={{ flex: 1 }}>{email}</div>
                <IconButton edge="end" aria-label="delete" onClick={(): void => removeEmail(idx)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <TextField
              type="email"
              label="Add email address"
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyDown={handleEmailInputKeyDown}
              variant="outlined"
              style={{ marginRight: 8, flex: 1 }}
              size="small"
              error={!!emailError}
              helperText={emailError}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={addEmail}
              disabled={
                !emailInput.trim() ||
                !validateEmail(emailInput.trim()) ||
                emails.includes(emailInput.trim())
              }
            >
              Add Email
            </Button>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <TextField
            type="number"
            label="Days"
            inputProps={{ min: 0 }}
            value={days}
            onChange={handleDaysChange}
            required
            margin="normal"
            variant="outlined"
            fullWidth
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={notified}
                onChange={e => setNotified(e.target.checked)}
                color="primary"
              />
            }
            label="Notified"
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
              This entry will be evaluated for email notification conditions every day at 10 AM.
            </Typography>
            {notificationUuid && onDelete && (
              <Button
                variant="contained"
                color="secondary"
                onClick={(): void => setDeleteDialogOpen(true)}
                style={{ marginLeft: 8 }}
              >
                Delete
              </Button>
            )}
          </div>
        </div>
      </form>
      <Dialog
        open={deleteDialogOpen}
        onClose={(): void => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the email notification. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={(): void => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              if (onDelete && notificationUuid) onDelete(notificationUuid);
            }}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AssignEmailNotificationForm;